/**
 * AuthService
 * Service for authentication operations
 * Route: api/auth
 */
import { BaseService } from '../base/BaseService';
/**
 * AuthService
 * Handles authentication operations including login, logout, token management, and password management
 */
export class AuthService extends BaseService {
    constructor(http, config) {
        const servicePath = config?.servicePath ?? AuthService.DEFAULT_SERVICE_PATH;
        console.log('[AuthService] Constructor - servicePath:', servicePath, 'basePath:', AuthService.BASE_PATH);
        super(http, {
            servicePath,
            basePath: AuthService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== LOGIN ====================
    /**
     * Authenticate user and generate JWT tokens
     * POST api/auth/login
     */
    async login(data) {
        const response = await this.loginRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Login failed');
        }
        return response.data;
    }
    /**
     * Login with full response
     */
    loginRaw(data) {
        const endpoint = this.getEndpoint('login', 'login');
        const url = this.buildUrl(endpoint);
        console.log('[AuthService.loginRaw] endpoint:', endpoint, 'url:', url);
        return this.http.post(url, data);
    }
    // ==================== VERIFY MFA ====================
    /**
     * Verify MFA code and complete login
     * POST api/auth/verify-mfa?userId={userId}
     */
    async verifyMfa(userId, data) {
        const response = await this.verifyMfaRaw(userId, data);
        if (!response.data) {
            throw new Error(response.message || 'MFA verification failed');
        }
        return response.data;
    }
    /**
     * Verify MFA with full response
     */
    verifyMfaRaw(userId, data) {
        const endpoint = this.getEndpoint('verify-mfa', 'verify-mfa');
        const url = this.buildUrlWithParams(endpoint, { userId });
        return this.http.post(url, data);
    }
    // ==================== REFRESH TOKEN ====================
    /**
     * Refresh access token using refresh token
     * POST api/auth/refresh
     */
    async refreshToken(data) {
        const response = await this.refreshTokenRaw(data);
        if (!response.data) {
            throw new Error(response.message || 'Token refresh failed');
        }
        return response.data;
    }
    /**
     * Refresh token with full response
     */
    refreshTokenRaw(data) {
        const endpoint = this.getEndpoint('refresh', 'refresh');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    // ==================== LOGOUT ====================
    /**
     * Logout user and invalidate tokens
     * POST api/auth/logout
     */
    async logout() {
        const response = await this.logoutRaw();
        return response.success;
    }
    /**
     * Logout with full response
     */
    logoutRaw() {
        const endpoint = this.getEndpoint('logout', 'logout');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== FORGOT PASSWORD ====================
    /**
     * Request password reset email
     * POST api/auth/forgot-password
     */
    async forgotPassword(data) {
        const response = await this.forgotPasswordRaw(data);
        return response.success;
    }
    /**
     * Forgot password with full response
     */
    forgotPasswordRaw(data) {
        const endpoint = this.getEndpoint('forgot-password', 'forgot-password');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    // ==================== VALIDATE RESET TOKEN ====================
    /**
     * Validate password reset token
     * GET api/auth/validate-reset-token?token={token}&email={email}
     */
    async validateResetToken(token, email) {
        const response = await this.validateResetTokenRaw(token, email);
        return response.success;
    }
    /**
     * Validate reset token with full response
     */
    validateResetTokenRaw(token, email) {
        const endpoint = this.getEndpoint('validate-reset-token', 'validate-reset-token');
        const url = this.buildUrlWithParams(endpoint, { token, email });
        return this.http.get(url);
    }
    // ==================== RESET PASSWORD ====================
    /**
     * Reset password using reset token
     * POST api/auth/reset-password
     */
    async resetPassword(data) {
        const response = await this.resetPasswordRaw(data);
        return response.success;
    }
    /**
     * Reset password with full response
     */
    resetPasswordRaw(data) {
        const endpoint = this.getEndpoint('reset-password', 'reset-password');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    // ==================== CHANGE PASSWORD ====================
    /**
     * Change password for authenticated user
     * POST api/auth/change-password
     */
    async changePassword(data) {
        const response = await this.changePasswordRaw(data);
        return response.success;
    }
    /**
     * Change password with full response
     */
    changePasswordRaw(data) {
        const endpoint = this.getEndpoint('change-password', 'change-password');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    // ==================== SEND MFA CODE ====================
    /**
     * Send MFA code to user's email
     * POST api/auth/send-mfa-code
     */
    async sendMfaCode() {
        const response = await this.sendMfaCodeRaw();
        if (!response.data) {
            throw new Error(response.message || 'Failed to send MFA code');
        }
        return response.data;
    }
    /**
     * Send MFA code with full response
     */
    sendMfaCodeRaw() {
        const endpoint = this.getEndpoint('send-mfa-code', 'send-mfa-code');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, {});
    }
    // ==================== PUBLIC SIGN-UP + EMAIL CONFIRMATION ====================
    /**
     * Public sign-up. Creates an unconfirmed account, assigns the "Member"
     * role, and triggers a confirmation email.
     * POST api/auth/register
     */
    async register(data) {
        const response = await this.registerRaw(data);
        return response.success;
    }
    registerRaw(data) {
        const endpoint = this.getEndpoint('register', 'register');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Confirm a user's email using the token sent at registration.
     * POST api/auth/confirm-email
     */
    async confirmEmail(data) {
        const response = await this.confirmEmailRaw(data);
        return response.success;
    }
    confirmEmailRaw(data) {
        const endpoint = this.getEndpoint('confirm-email', 'confirm-email');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Re-emit a confirmation email if the user is still pending.
     * Always resolves successfully — never leaks whether the address exists.
     * POST api/auth/resend-confirmation
     */
    async resendConfirmation(data) {
        const response = await this.resendConfirmationRaw(data);
        return response.success;
    }
    resendConfirmationRaw(data) {
        const endpoint = this.getEndpoint('resend-confirmation', 'resend-confirmation');
        const url = this.buildUrl(endpoint);
        return this.http.post(url, data);
    }
    /**
     * Returns the current EmailConfirmed flag for the supplied email.
     * Used by the resource centre banner to clear itself without re-login.
     * Returns false for unknown / unconfirmed emails — never leaks existence.
     * GET api/auth/email-confirmation-status?email={email}
     */
    async getEmailConfirmationStatus(email) {
        const response = await this.getEmailConfirmationStatusRaw(email);
        return response.data === true;
    }
    getEmailConfirmationStatusRaw(email) {
        const endpoint = this.getEndpoint('email-confirmation-status', 'email-confirmation-status');
        const url = this.buildUrlWithParams(endpoint, { email });
        return this.http.get(url);
    }
}
AuthService.DEFAULT_SERVICE_PATH = 'users';
AuthService.BASE_PATH = 'api/auth';
//# sourceMappingURL=AuthService.js.map